import { useId, type ReactNode } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import { useIteroOrder } from '../../../../../hooks/useIteroBrowse';
import type { IteroOrder } from '../../../../../services/iteroBrowse';
import { browseErrorText } from '../browseErrorText';
import { useIteroBrowserScope } from '../iteroBrowserScope';
import { ScansTab } from '../scan-preview/ScansTab';
import { scansCountLabel } from '../scan-preview/scanTally';
import { useScanImageSets } from '../scan-preview/useScanImageSets';
import { useLatchedFlag } from '../useLatchedFlag';
import { OrderContextLine } from './OrderContextLine';
import { OrderDetailsTab } from './OrderDetailsTab';
import { OrderTabs } from './OrderTabs';
import { orderPanelId, orderTabId, type OrderTabId } from './orderTabIds';

interface TabPanelProps {
    baseId: string;
    tab: OrderTabId;
    isActive: boolean;
    children: ReactNode;
}

// Each tab keeps its own scroll position, and a hidden Scans tab keeps its downloads running.
function TabPanel({ baseId, tab, isActive, children }: TabPanelProps) {
    return (
        <div
            id={orderPanelId(baseId, tab)}
            role="tabpanel"
            aria-labelledby={orderTabId(baseId, tab)}
            className={isActive ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}
        >
            <OverlayScrollArea className="min-h-0 flex-1">{children}</OverlayScrollArea>
        </div>
    );
}

interface OrderViewProps {
    order: IteroOrder;
    tab: OrderTabId;
    onTabChange: (tab: OrderTabId) => void;
    // Present only when the order was opened from a patient's scan history.
    onBack?: () => void;
    focusOnMount?: boolean;
}

// Fetching the single order (useIteroOrder) is what records the view in the audit log.
export function OrderView({
    order: listOrder,
    tab: activeTab,
    onTabChange,
    onBack,
    focusOnMount = false
}: OrderViewProps) {
    const scope = useIteroBrowserScope();
    const query = useIteroOrder(scope, listOrder.rxId, listOrder);
    const order = query.data ?? listOrder;
    const baseId = useId().replace(/:/g, '');
    const scansOpened = useLatchedFlag(activeTab === 'scans');
    const images = useScanImageSets(order.rxId, scansOpened);
    const scansCount = scansCountLabel([
        images['model-views'].tally,
        images['screen-captures'].tally
    ]);

    const openScans = () => {
        onTabChange('scans');
        document.getElementById(orderTabId(baseId, 'scans'))?.focus();
    };

    return (
        <>
            <OrderContextLine order={order} onBack={onBack} focusOnMount={focusOnMount} />
            <OrderTabs
                baseId={baseId}
                activeTab={activeTab}
                scansCount={scansCount}
                onChange={onTabChange}
            />
            <TabPanel baseId={baseId} tab="details" isActive={activeTab === 'details'}>
                <OrderDetailsTab
                    order={order}
                    refreshError={
                        query.isError
                            ? browseErrorText(
                                  query.error,
                                  "Couldn't refresh this order. Showing the list details."
                              )
                            : null
                    }
                    onRetryRefresh={() => void query.refetch()}
                    onOpenScans={openScans}
                />
            </TabPanel>
            {scansOpened && (
                <TabPanel baseId={baseId} tab="scans" isActive={activeTab === 'scans'}>
                    <ScansTab order={order} images={images} />
                </TabPanel>
            )}
        </>
    );
}
